/**
 * Users List Page
 * Main page for listing and managing users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ToggleOff as InactivateIcon,
  ToggleOn as ActivateIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { UserFilters } from './components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { RouteActionButton } from '../../components/common';

// Services and types
import { userService } from '../../services/userService';
import {
  User,
  UserFilters as UserFiltersType,
  UserStatus,
  USER_STATUS_LABELS,
  getUserStatusColor,
  formatCPF,
  getRoleLabel,
} from '../../types/user';

// Hooks

interface UserFiltersData {
  searchTerm: string;
  situacao: string[];
}

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 20;

// Check if user is active
const isUserActive = (user: User) => user.situacao === UserStatus.ATIVO;

// Get user initials for avatar
const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const columns: DataTableColumn<User>[] = [
  {
    id: 'user',
    label: 'Usuário',
    primary: true,
    render: (user) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          src={user.avatarPath ?? undefined}
          sx={{
            bgcolor: 'primary.main',
            width: 32,
            height: 32,
          }}
        >
          {getInitials(user.name)}
        </Avatar>
        <Typography variant="subtitle2" fontWeight="bold">
          {user.name}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'email',
    label: 'E-mail',
    render: (user) => <Typography variant="body2">{user.email}</Typography>,
  },
  {
    id: 'cpf',
    label: 'CPF',
    render: (user) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatCPF(user.cpf)}
      </Typography>
    ),
  },
  {
    id: 'roles',
    label: 'Papéis',
    render: (user) => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {user.roles && user.roles.length > 0 ? (
          user.roles.slice(0, 2).map((role) => (
            <Chip
              key={role.id}
              label={role.displayName || getRoleLabel(role.name)}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.75rem' }}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
        {user.roles && user.roles.length > 2 && (
          <Tooltip title={user.roles.slice(2).map((r) => r.displayName || getRoleLabel(r.name)).join(', ')}>
            <Chip
              label={`+${user.roles.length - 2}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          </Tooltip>
        )}
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (user) => (
      <Chip
        label={USER_STATUS_LABELS[user.situacao]}
        size="small"
        color={getUserStatusColor(user.situacao)}
      />
    ),
  },
];

const UsersListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const visualizar = useControle('/users/:id', pode('user.read'), 'Visualizar');
  const editar = useControle('/users/:id/edit', pode('user.update'), 'Editar');
  const desativar = useAcao('user.update', 'Desativar');
  const ativar = useAcao('user.update', 'Ativar');

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<UserFiltersType>({});
  const [search, setSearch] = useState('');

  // Toggle status confirmation dialog state
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState<User | null>(null);

  // Load users from API
  const loadUsers = useCallback(
    async (
      pageNumber: number,
      limit: number,
      searchTerm: string,
      userFilters: UserFiltersType
    ) => {
      setLoading(true);
      setError(null);

      try {
        console.log('[UsersListPage] Loading users:', { pageNumber, limit, searchTerm, userFilters });

        const response = await userService.getUsers({
          page: pageNumber,
          limit,
          search: searchTerm,
          filters: userFilters,
          sortBy: 'name',
          sortOrder: 'ASC',
        });

        setUsers(response.data);
        setTotalCount(response.pagination.total);

        console.log('[UsersListPage] Users loaded:', response);
      } catch (err: unknown) {
        console.error('[UsersListPage] Error loading users:', err);
        const errorMessage = (err as { message?: string }).message ?? 'Erro ao carregar usuários';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  // Load on mount
  useEffect(() => {
    loadUsers(INITIAL_PAGE, INITIAL_ROWS_PER_PAGE, '', {});
  }, [loadUsers]);

  // Handle inline filters change (search + situação aplicam ao vivo)
  const handleApplyFilters = (data: UserFiltersData) => {
    const apiFilters: UserFiltersType = {};
    if (data.situacao.length > 0) {
      apiFilters.situacao = data.situacao as UserStatus[];
    }

    setFilters(apiFilters);
    setSearch(data.searchTerm);
    setPage(1);
    loadUsers(1, rowsPerPage, data.searchTerm, apiFilters);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1); // MUI uses 0-based, API uses 1-based
    loadUsers(newPage + 1, rowsPerPage, search, filters);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    loadUsers(1, newRowsPerPage, search, filters);
  };

  // Handle view user
  const handleViewUser = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    navigate(`/users/${user.id}/edit`);
  };

  // Handle toggle status click - opens confirmation dialog
  const handleToggleStatusClick = (user: User) => {
    setUserToToggle(user);
    setToggleDialogOpen(true);
  };

  // Handle toggle status cancel
  const handleToggleStatusCancel = () => {
    setToggleDialogOpen(false);
    setUserToToggle(null);
  };

  // Handle toggle user status
  const handleToggleUserStatus = async (user: User) => {
    try {
      console.log('[UsersListPage] Toggling user status:', user.id);

      await userService.toggleUserStatus(user.id, user.situacao);

      const action = user.situacao === UserStatus.ATIVO ? 'desativado' : 'ativado';
      enqueueSnackbar(`Usuário ${action} com sucesso`, { variant: 'success' });

      // Reload users
      loadUsers(page, rowsPerPage, search, filters);
    } catch (err: unknown) {
      console.error('[UsersListPage] Error toggling user status:', err);
      const errorMessage = (err as { message?: string }).message ?? 'Erro ao alterar status do usuário';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle toggle status confirm
  const handleToggleStatusConfirm = () => {
    if (userToToggle && !ativar.disabled) {
      handleToggleUserStatus(userToToggle);
    }
    setToggleDialogOpen(false);
    setUserToToggle(null);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Usuários' },
      ]}
      title="Usuários"
      actions={
        <RouteActionButton
          para="/users/create"
          exige={pode('user.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Novo Usuário
        </RouteActionButton>
      }
    >
      {/* Filters */}
      <UserFilters onFilter={handleApplyFilters} loading={loading} total={totalCount} />

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <DataTable
        columns={columns}
        rows={users}
        getRowId={(user) => user.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum usuário encontrado',
          description:
            'Os usuários são criados nesta tela e aparecem conforme as empresas do seu acesso.',
        }}
        actions={[
          {
            icon: <VisibilityIcon fontSize="small" />,
            onClick: handleViewUser,
            ...visualizar,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEditUser,
            ...editar,
          },
          {
            icon: <InactivateIcon fontSize="small" />,
            color: 'warning',
            onClick: handleToggleStatusClick,
            hidden: (user) => !isUserActive(user),
            ...desativar,
          },
          {
            icon: <ActivateIcon fontSize="small" />,
            color: 'success',
            onClick: handleToggleStatusClick,
            hidden: (user) => isUserActive(user),
            ...ativar,
          },
        ]}
        pagination={{
          page: page - 1, // Convert to 0-based for MUI
          rowsPerPage,
          totalCount,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
      />

      {/* Toggle Status Confirmation Dialog */}
      <Dialog open={toggleDialogOpen} onClose={handleToggleStatusCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmar {userToToggle && isUserActive(userToToggle) ? 'Desativação' : 'Ativação'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja {userToToggle && isUserActive(userToToggle) ? 'desativar' : 'ativar'}{' '}
            o usuário <strong>{userToToggle?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {userToToggle && isUserActive(userToToggle)
              ? 'O usuário não poderá acessar o sistema enquanto estiver desativado.'
              : 'O usuário poderá acessar o sistema novamente.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleToggleStatusCancel}>Cancelar</Button>
          <Button
            onClick={handleToggleStatusConfirm}
            color={userToToggle && isUserActive(userToToggle) ? 'warning' : 'success'}
            variant="contained"
          >
            {userToToggle && isUserActive(userToToggle) ? 'Desativar' : 'Ativar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default UsersListPage;
